import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Youtube, Mail, Phone, Lock, CheckCircle, ArrowRight } from 'lucide-react';
import { contactInfo } from '../utils/mockData';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-black border-t border-white/10" data-testid="site-footer">
      {/* Newsletter / mini-CTA strip */}
      <div className="relative border-b border-white/10 py-10">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="text-white font-editorial text-2xl md:text-3xl leading-tight">
                Ready to make your memories cinematic?
              </div>
              <p className="text-gray-400 mt-2 text-sm">
                A booking deposit of £20, £30 or £50 secures your date. The remaining balance is due before final delivery.{' '}
                <Link to="/terms-conditions#refunds" data-testid="footer-refund-link" className="underline underline-offset-2 text-amber-300/90 hover:text-amber-200">
                  Cancellations &amp; refunds
                </Link>
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
              <Link
                to="/pricing"
                data-testid="footer-cta-book"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-full hover:from-amber-600 hover:to-amber-700 transition-all duration-300 shadow-lg"
              >
                Book My Cinematic Film
                <ArrowRight size={16} />
              </Link>
              <a
                href={`https://wa.me/${contactInfo.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 border border-white/20 text-white/90 font-medium rounded-full hover:border-white/50 hover:bg-white/5 transition-all duration-300"
              >
                Talk to Simon
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Trust badges */}
      <div className="border-b border-white/10 py-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-3">
              <Lock className="text-amber-400 flex-shrink-0" size={26} strokeWidth={1.5} />
              <div className="text-left">
                <div className="font-semibold text-white text-sm">Secure Payment</div>
                <div className="text-xs text-gray-500">SSL Encrypted · Stripe</div>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3">
              <CheckCircle className="text-amber-400 flex-shrink-0" size={26} strokeWidth={1.5} />
              <div className="text-left">
                <div className="font-semibold text-white text-sm">Revisions Included</div>
                <div className="text-xs text-gray-500">1, 2 or 3 free revisions depending on your collection</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main columns */}
      <div className="container mx-auto px-4 lg:px-8 py-14">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 mb-10">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-2 space-y-4">
            <img
              src="https://customer-assets.emergentagent.com/job_slideshow-studio-5/artifacts/3f84c0r9_file_00000000e84c71f4bc5168c8fafe2f26.png"
              alt="Visual Creationz"
              className="h-14 md:h-16 w-auto"
            />
            <p className="text-gray-400 text-sm leading-relaxed max-w-sm">
              Cinematic memory films crafted from your photos, videos and music. Bespoke storytelling for weddings, memorials, birthdays and life's most meaningful moments - proudly made in the UK.
            </p>
            <div className="flex gap-2 pt-2">
              <a
                href="https://www.facebook.com/visualcreationz"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/80 hover:border-amber-500 hover:bg-amber-500 hover:text-black transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={16} />
              </a>
              <a
                href="https://www.instagram.com/visualcreationz"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/80 hover:border-amber-500 hover:bg-amber-500 hover:text-black transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={16} />
              </a>
              <a
                href="https://www.tiktok.com/@visual.creationz"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/80 hover:border-amber-500 hover:bg-amber-500 hover:text-black transition-colors"
                aria-label="TikTok"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
              <a
                href="https://www.youtube.com/@visualcreationz"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/80 hover:border-amber-500 hover:bg-amber-500 hover:text-black transition-colors"
                aria-label="YouTube"
              >
                <Youtube size={16} />
              </a>
            </div>
          </div>

          {/* Explore */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-xs uppercase tracking-[0.25em]">Explore</h3>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/samples" className="text-gray-400 hover:text-amber-400 transition-colors">Portfolio</Link></li>
              <li><Link to="/services" className="text-gray-400 hover:text-amber-400 transition-colors">Services</Link></li>
              <li><Link to="/how-it-works" className="text-gray-400 hover:text-amber-400 transition-colors">How It Works</Link></li>
              <li><Link to="/pricing" className="text-gray-400 hover:text-amber-400 transition-colors">Pricing</Link></li>
              <li><Link to="/about" className="text-gray-400 hover:text-amber-400 transition-colors">About</Link></li>
              <li><Link to="/contact" className="text-gray-400 hover:text-amber-400 transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-xs uppercase tracking-[0.25em]">Services</h3>
            <ul className="space-y-2.5 text-sm text-gray-400">
              <li>Wedding Films</li>
              <li>Memorial Tributes</li>
              <li>Birthday Celebrations</li>
              <li>Anniversary Videos</li>
              <li>Corporate & Brand Films</li>
              <li>Bespoke Projects</li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-xs uppercase tracking-[0.25em]">Get In Touch</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href={`tel:${contactInfo.phone}`}
                  className="flex items-center gap-2 text-gray-400 hover:text-amber-400 transition-colors group"
                >
                  <Phone size={14} className="text-amber-500/80 group-hover:text-amber-400" strokeWidth={1.75} />
                  <span>{contactInfo.phone}</span>
                </a>
                <p className="text-gray-600 text-xs mt-1 ml-6">Mon-Sat, 9am-6pm</p>
              </li>
              <li>
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="flex items-start gap-2 text-gray-400 hover:text-amber-400 transition-colors group"
                >
                  <Mail size={14} className="text-amber-500/80 group-hover:text-amber-400 flex-shrink-0 mt-1" strokeWidth={1.75} />
                  <span className="min-w-0 break-all">{contactInfo.email}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-xs">
              © {currentYear} Visual Creationz. All rights reserved.
            </p>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-gray-500">
              <Link to="/privacy-policy" className="hover:text-amber-400 transition-colors">Privacy Policy</Link>
              <Link to="/terms-conditions" className="hover:text-amber-400 transition-colors">Terms &amp; Conditions</Link>
              <span className="text-gray-700">·</span>
              <span className="italic">Moments. Made Cinematic.</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
